import React from 'react';
import { UserDetailData } from '../types/analytics.types';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './UserDetailModal.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface UserDetailModalProps {
  userId: string;
  data: UserDetailData | null;
  loading: boolean;
  onClose: () => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({ userId, data, loading, onClose }) => {
  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content user-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Loading User Details...</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="loading-skeleton" style={{ height: '400px' }}></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content user-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>User Details</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <p className="no-data">No data available for this user</p>
        </div>
      </div>
    );
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing': return '📈';
      case 'decreasing': return '📉';
      case 'stable': return '➡️';
      default: return '❓';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'increasing': return '#28a745';
      case 'decreasing': return '#dc3545';
      case 'stable': return '#6c757d';
      default: return '#6c757d';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>User Analytics</h3>
            <p className="user-id-display">User ID: <strong>{data.user_id}</strong></p>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Summary Cards */}
          <div className="summary-grid">
            <div className="summary-card">
              <div className="card-label">Total Actions</div>
              <div className="card-value">{data.total_actions.toLocaleString()}</div>
            </div>
            <div className="summary-card">
              <div className="card-label">Total Sessions</div>
              <div className="card-value">{data.session_metrics.total_sessions}</div>
            </div>
            <div className="summary-card">
              <div className="card-label">Accuracy</div>
              <div className="card-value">{data.performance_metrics.accuracy_percent.toFixed(1)}%</div>
            </div>
            <div className="summary-card">
              <div className="card-label">Avg Session (min)</div>
              <div className="card-value">{data.session_metrics.avg_session_duration_minutes.toFixed(1)}</div>
            </div>
          </div>

          {/* Activity Chart */}
          <div className="chart-section">
            <h4>Activity Over Time</h4>
            <div style={{ height: '250px' }}>
              <Line
                data={{
                  labels: data.activity_by_date.map(item => item.date),
                  datasets: [{
                    label: 'Actions',
                    data: data.activity_by_date.map(item => item.actions),
                    borderColor: '#8884d8',
                    backgroundColor: 'rgba(136, 132, 216, 0.1)',
                    tension: 0.4
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: true, position: 'top' }
                  },
                  scales: {
                    y: { beginAtZero: true }
                  }
                }}
              />
            </div>
          </div>

          {/* Daily Sessions Chart */}
          {data.sessions_by_date && data.sessions_by_date.length > 0 && (
            <div className="chart-section">
              <h4>Daily Sessions</h4>
              <div style={{ height: '250px' }}>
                <Bar
                  data={{
                    labels: data.sessions_by_date.map(item => item.date),
                    datasets: [{
                      label: 'Sessions',
                      data: data.sessions_by_date.map(item => item.session_count),
                      backgroundColor: '#4A90E2'
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      y: { beginAtZero: true },
                      x: { ticks: { maxRotation: 45, minRotation: 45 } }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Daily Time Spent Chart */}
          {data.sessions_by_date && data.sessions_by_date.length > 0 && (
            <div className="chart-section">
              <h4>Daily Time Spent (min)</h4>
              <div style={{ height: '250px' }}>
                <Bar
                  data={{
                    labels: data.sessions_by_date.map(item => item.date),
                    datasets: [{
                      label: 'Minutes',
                      data: data.sessions_by_date.map(item => item.duration_minutes),
                      backgroundColor: '#50C878'
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    scales: {
                      y: { beginAtZero: true },
                      x: { ticks: { maxRotation: 45, minRotation: 45 } }
                    }
                  }}
                />
              </div>
            </div>
          )}

          <div className="two-column-layout">
            {/* Component Breakdown */}
            <div className="chart-section">
              <h4>Activity by Component</h4>
              <div style={{ height: '250px' }}>
                <Bar
                  data={{
                    labels: data.component_breakdown.map(item => item.component),
                    datasets: [{
                      label: 'Actions',
                      data: data.component_breakdown.map(item => item.actions),
                      backgroundColor: '#82ca9d'
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'top' }
                    },
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }}
                />
              </div>
            </div>

            {/* Section Progress */}
            <div className="chart-section">
              <h4>Activity by Section</h4>
              <div style={{ height: '250px' }}>
                <Bar
                  data={{
                    labels: data.section_progress.slice(0, 10).map(item => item.section),
                    datasets: [{
                      label: 'Actions',
                      data: data.section_progress.slice(0, 10).map(item => item.actions),
                      backgroundColor: '#ffc658'
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'top' }
                    },
                    scales: {
                      y: { beginAtZero: true },
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="metrics-section">
            <h4>Performance Metrics</h4>
            <div className="metrics-grid">
              <div className="metric-item">
                <span className="metric-label">Total Answers:</span>
                <span className="metric-value">{data.performance_metrics.total_answers}</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Correct Answers:</span>
                <span className="metric-value correct">{data.performance_metrics.correct_answers}</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Incorrect Answers:</span>
                <span className="metric-value incorrect">{data.performance_metrics.incorrect_answers}</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Avg Response Time:</span>
                <span className="metric-value">{data.performance_metrics.avg_response_time_ms.toFixed(0)}ms</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Avg Actions/Session:</span>
                <span className="metric-value">{data.session_metrics.avg_actions_per_session.toFixed(1)}</span>
              </div>
            </div>
          </div>

          {/* Learning Velocity */}
          <div className="velocity-section">
            <h4>Learning Velocity</h4>
            <div className="velocity-summary">
              <div className="velocity-item">
                <span className="velocity-label">Avg Actions/Week:</span>
                <span className="velocity-value">{data.learning_velocity.avg_actions_per_week.toFixed(1)}</span>
              </div>
              <div className="velocity-item">
                <span className="velocity-label">Weeks Active:</span>
                <span className="velocity-value">{data.learning_velocity.total_weeks_active}</span>
              </div>
              <div className="velocity-item">
                <span className="velocity-label">Trend:</span>
                <span className="velocity-value" style={{ color: getTrendColor(data.learning_velocity.trend) }}>
                  {getTrendIcon(data.learning_velocity.trend)} {data.learning_velocity.trend}
                </span>
              </div>
            </div>

            {data.learning_velocity.weekly_breakdown.length > 0 && (
              <div style={{ height: '200px', marginTop: '20px' }}>
                <Line
                  data={{
                    labels: data.learning_velocity.weekly_breakdown.map(item => item.week_start),
                    datasets: [{
                      label: 'Weekly Actions',
                      data: data.learning_velocity.weekly_breakdown.map(item => item.actions),
                      borderColor: '#8884d8',
                      backgroundColor: 'rgba(136, 132, 216, 0.1)',
                      tension: 0.4
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'top' }
                    },
                    scales: {
                      y: { beginAtZero: true },
                      x: {
                        ticks: {
                          maxRotation: 45,
                          minRotation: 45
                        }
                      }
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
